import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileText, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { isUgc } from "@/components/portal/PlatformBits";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate, formatDateTime, formatRM, todayKL } from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import {
  Attachment, Badge, Button, Card, Empty, ErrorNote, Field, Loading, Modal, Note, inputClass,
} from "@/components/portal/Ui";
import { Uploader } from "@/components/portal/Uploader";

function BillingCard({ campaigns }) {
  const [campaignId, setCampaignId] = useState("");
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await api.get("/invoices/template", {
        params: { campaign_id: campaignId || undefined },
        responseType: "blob",
      });
      const match = (response.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1] : "anytribe-invoice-template.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Invoice template downloaded");
    } catch (e) {
      toast.error(apiError(e, "The template could not be generated. Please retry."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-5" testId="billing-info-card">
      <h2 className="text-lg font-extrabold">Payment timeline</h2>
      <p className="mt-2 text-sm leading-relaxed body-text" data-testid="payment-timeline-note">
        Payment will be processed within 30 days after we receive a complete submission or after the
        campaign end date, whichever is later.
      </p>

      <div className="mt-5 rounded-xl border border-[#EFEAE1] bg-[#FBF9F5] p-4">
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
          Our billing information (for invoice issuance)
        </p>
        <dl className="mt-3 space-y-1.5 text-sm" data-testid="billing-details">
          <div className="flex flex-wrap gap-x-2">
            <dt className="font-semibold">Company Name:</dt>
            <dd className="body-text">AnyTribe Marketing</dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="font-semibold">Address:</dt>
            <dd className="body-text">231, Taman Mutiara, Sungai Petani 08000 Kedah</dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="font-semibold">Email:</dt>
            <dd className="body-text">AnyTribe.co@gmail.com</dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="font-semibold">Attn:</dt>
            <dd className="body-text">Ulrick</dd>
          </div>
        </dl>
        <p className="mt-3 text-xs font-semibold body-text">
          (Please ensure the above details are correctly stated on your invoice.)
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <Field label="Invoice template" hint="Pre-filled with our billing details and your name. Pick a campaign to include it.">
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className={inputClass}
              data-testid="template-campaign-select"
            >
              <option value="">No specific campaign</option>
              {(campaigns || []).map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Button variant="outline" busy={busy} onClick={downloadTemplate} testId="download-invoice-template">
          <Download className="h-4 w-4" />
          Download template
        </Button>
      </div>
    </Card>
  );
}

export default function CreatorInvoices() {
  const [params, setParams] = useSearchParams();
  const [invoices, setInvoices] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [readiness, setReadiness] = useState([]);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [modal, setModal] = useState(null);
  const [generate, setGenerate] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [i, c, r] = await Promise.all([
        api.get("/invoices"),
        api.get("/campaigns"),
        api.get("/invoices/readiness"),
      ]);
      setInvoices(i.data);
      setCampaigns(c.data);
      setReadiness(r.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setInvoices((prev) => prev || []);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  useEffect(() => {
    const campaignId = params.get("campaign");
    if (campaignId && campaigns.length) {
      setModal({ mode: "new", campaignId });
      params.delete("campaign");
      setParams(params, { replace: true });
    }
  }, [campaigns, params, setParams]);

  if (invoices === null) return <Loading label="Loading your invoices…" />;

  return (
    <div data-testid="creator-invoices-page">
      <PageHeader
        title="My Invoices"
        description="One invoice can cover several campaigns. You can send a corrected version any time AnyTribe requests changes."
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button testId="generate-invoice-button" onClick={() => setGenerate(true)}>
              <FileText className="h-4 w-4" />
              Generate my invoice
            </Button>
            <Button variant="outline" testId="new-invoice-button" onClick={() => setModal({ mode: "new" })}>
              <Plus className="h-4 w-4" />
              Upload my own
            </Button>
          </div>
        }
      />
      <ErrorNote message={error} onRetry={load} />

      <BillingCard campaigns={campaigns} />

      <Card className="mb-5" testId="readiness-card">
        <h2 className="text-lg font-extrabold">Invoice readiness</h2>
        <p className="mt-1 text-sm body-text">
          You can invoice a campaign once every post you submitted has insights with a screenshot,
          and you've submitted the number of posts the campaign expects.
        </p>
        {readiness.length === 0 ? (
          <p className="mt-3 text-sm body-text">
            You're not assigned to any campaign yet.
          </p>
        ) : (
          <ul className="mt-3 space-y-2.5">
            {readiness.map((row) => (
              <li
                key={row.campaign_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#EFEAE1] p-3.5"
                data-testid={`readiness-row-${row.campaign_id}`}
              >
                <div>
                  <p className="text-sm font-semibold">{row.campaign_name}</p>
                  <p className="mt-0.5 text-xs body-text">
                    {row.published_posts} post{row.published_posts === 1 ? "" : "s"} submitted
                    {row.expected_posts ? ` of ${row.expected_posts} expected` : ""}
                  </p>
                </div>
                {row.ready ? (
                  <Badge tone="green" testId={`readiness-status-${row.campaign_id}`}>
                    Ready to invoice
                  </Badge>
                ) : (
                  <div className="text-right">
                    <Badge tone="orange" testId={`readiness-status-${row.campaign_id}`}>
                      {row.blockers.join(" · ")}
                    </Badge>
                    <Link
                      to="/creator"
                      className="mt-1 block text-xs font-semibold text-[#F2811D] hover:underline"
                      data-testid={`readiness-fix-${row.campaign_id}`}
                    >
                      Complete the reporting
                    </Link>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {invoices.length === 0 ? (
        <Empty
          testId="creator-no-invoices"
          title="No invoices submitted yet"
          body="You can submit an invoice as soon as you're assigned to a campaign — you don't need to finish posting first."
          action={
            <Button testId="empty-new-invoice-button" onClick={() => setGenerate(true)}>
              Generate my invoice
            </Button>
          }
        />
      ) : (
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {invoices.map((invoice) => (
            <Card key={invoice.id} testId={`creator-invoice-${invoice.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
                    {(invoice.campaign_names || [invoice.campaign_name]).join(" + ")}
                    {invoice.is_consolidated && (
                      <span className="ml-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#F2811D]" data-testid={`consolidated-tag-${invoice.id}`}>
                        {invoice.lines.length} campaigns
                      </span>
                    )}
                  </p>
                  <h2 className="mt-1 text-lg font-extrabold" data-testid={`invoice-number-${invoice.id}`}>
                    {invoice.invoice_number}
                  </h2>
                </div>
                <Badge testId={`invoice-status-${invoice.id}`}>{invoice.status}</Badge>
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">Amount</dt>
                  <dd className="mt-1 text-lg font-extrabold tabular" data-testid={`invoice-amount-${invoice.id}`}>
                    {formatRM(invoice.amount_sen)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">Invoice date</dt>
                  <dd className="mt-1 text-sm font-semibold">{formatDate(invoice.invoice_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">Due date</dt>
                  <dd className="mt-1 text-sm font-semibold">{formatDate(invoice.due_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                    {invoice.status === "Paid" ? "Paid on" : "Version"}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold">
                    {invoice.status === "Paid" ? formatDate(invoice.paid_at) : `v${invoice.version}`}
                  </dd>
                </div>
              </dl>

              <div className="mt-4">
                <Attachment file={invoice.attachment} testId={`invoice-attachment-${invoice.id}`} />
              </div>

              {invoice.feedback?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {invoice.feedback.map((note) => (
                    <Note key={note.id} tone="red" testId={`invoice-feedback-${note.id}`}>
                      <strong>{note.author_name}:</strong> {note.message}
                    </Note>
                  ))}
                </div>
              )}

              {invoice.status === "Approved" && (
                <Note tone="green" testId={`invoice-approved-note-${invoice.id}`}>
                  Approved by {invoice.approved_by_name} on {formatDate(invoice.approved_at)}. This
                  invoice can no longer be edited.
                </Note>
              )}
              {invoice.status === "Paid" && (
                <Note tone="green">
                  Payment recorded {formatDate(invoice.paid_at)}
                  {invoice.payment_reference ? ` · ref ${invoice.payment_reference}` : ""}.
                </Note>
              )}

              <p className="mt-4 text-xs body-text">
                Submitted {formatDateTime(invoice.submitted_at)} by {invoice.submitted_by_name}
              </p>

              {["Submitted", "Changes requested"].includes(invoice.status) && (
                <div className="mt-4">
                  <Button
                    variant="outline"
                    testId={`resubmit-invoice-button-${invoice.id}`}
                    onClick={() => setModal({ mode: "version", invoice })}
                  >
                    Submit corrected version
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <InvoiceModal
        state={modal}
        campaigns={campaigns}
        readiness={readiness}
        onClose={() => setModal(null)}
        onSaved={async () => {
          setModal(null);
          await load();
        }}
      />

      <GenerateInvoiceModal
        open={generate}
        campaigns={campaigns}
        readiness={readiness}
        onClose={() => setGenerate(false)}
        onSaved={async () => {
          setGenerate(false);
          await load();
        }}
      />
    </div>
  );
}

export function InvoiceModal({ state, campaigns, onClose, onSaved, creatorsByCampaign, readiness }) {
  const isVersion = state?.mode === "version";
  const [campaignId, setCampaignId] = useState("");
  const [creatorId, setCreatorId] = useState("");
  const [number, setNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayKL());
  const [dueDate, setDueDate] = useState("");
  const [amount, setAmount] = useState("");
  const [lines, setLines] = useState({});
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploaderKey, setUploaderKey] = useState(0);

  useEffect(() => {
    if (!state) return;
    setCampaignId(state.campaignId || state.invoice?.campaign_id || "");
    setCreatorId(state.creatorId || "");
    setNumber(state.invoice?.invoice_number || "");
    setInvoiceDate(state.invoice?.invoice_date || todayKL());
    setDueDate(state.invoice?.due_date || "");
    setAmount(state.invoice ? (state.invoice.amount_sen / 100).toFixed(2) : "");
    setLines(state.campaignId ? { [state.campaignId]: "" } : {});
    setNote("");
    setFiles([]);
    setError("");
    setUploaderKey((k) => k + 1);
  }, [state]);

  const selectedLines = Object.entries(lines)
    .filter(([, amt]) => amt !== undefined && amt !== null)
    .map(([id, amt]) => {
      const campaign = (campaigns || []).find((c) => c.id === id);
      return { id, amount: amt, name: campaign?.name || "Campaign", campaign };
    });
  const totalRM = selectedLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const creatorOptions = creatorsByCampaign?.[selectedLines[0]?.id || campaignId] || null;

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (files.length === 0) {
      setError("Attach the invoice as a PDF document.");
      return;
    }
    if (!isVersion) {
      if (selectedLines.length === 0) {
        setError("Select at least one campaign to invoice.");
        return;
      }
      const bad = selectedLines.filter((l) => !(Number(l.amount) > 0));
      if (bad.length > 0) {
        setError(`Enter the amount for ${bad.map((l) => l.name).join(", ")}.`);
        return;
      }
    }
    setBusy(true);
    setError("");
    try {
      if (isVersion) {
        await api.post(`/invoices/${state.invoice.id}/versions`, {
          invoice_date: invoiceDate,
          due_date: dueDate || null,
          amount,
          note: note.trim() || null,
          file_id: files[0].id,
        });
        toast.success("Corrected invoice submitted");
      } else {
        await api.post("/invoices", {
          lines: selectedLines.map((l) => ({ campaign_id: l.id, amount: Number(l.amount) })),
          creator_id: creatorId || undefined,
          invoice_number: number.trim(),
          invoice_date: invoiceDate,
          due_date: dueDate || null,
          note: note.trim() || null,
          file_id: files[0].id,
        });
        toast.success(
          selectedLines.length > 1
            ? `Invoice submitted for ${selectedLines.length} campaigns`
            : "Invoice submitted"
        );
      }
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(state)}
      onClose={onClose}
      title={isVersion ? "Submit a corrected invoice" : "Submit an invoice"}
      testId="invoice-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="invoice-form">
        <ErrorNote message={error} />
        {isVersion && (
          <Note tone="neutral">
            This adds a new version to invoice {state.invoice.invoice_number}. Earlier versions and
            feedback stay on the record.
          </Note>
        )}

        {!isVersion && (
          <>
            <Field
              label="Campaigns on this invoice"
              required
              hint="Tick every campaign this invoice covers and enter each amount. One invoice can cover several campaigns."
            >
              <div className="space-y-2" data-testid="invoice-campaign-lines">
                {campaigns.map((campaign) => {
                  const selected = lines[campaign.id] !== undefined;
                  const mine = (campaign.creators || [])[0];
                  const outstanding = isUgc(campaign.category)
                    ? 0
                    : mine?.insights_outstanding ?? 0;
                  const gate = readiness
                    ? readiness.find((r) => r.campaign_id === campaign.id)
                    : null;
                  const blocked = Boolean(gate && !gate.ready);
                  return (
                    <div
                      key={campaign.id}
                      className={`rounded-xl border p-3 transition-colors ${
                        blocked
                          ? "border-[#EFEAE1] bg-[#FBFAF7] opacity-80"
                          : selected
                            ? "border-[#F2811D] bg-[#FFF8F1]"
                            : "border-[#EFEAE1]"
                      }`}
                      data-testid={`invoice-line-${campaign.id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm font-semibold">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={blocked}
                            onChange={(e) =>
                              setLines((prev) => {
                                const next = { ...prev };
                                if (e.target.checked) next[campaign.id] = "";
                                else delete next[campaign.id];
                                return next;
                              })
                            }
                            data-testid={`invoice-line-check-${campaign.id}`}
                          />
                          {campaign.name}
                        </label>
                        {selected && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold body-text">RM</span>
                            <input
                              inputMode="decimal"
                              value={lines[campaign.id]}
                              onChange={(e) =>
                                setLines((prev) => ({ ...prev, [campaign.id]: e.target.value }))
                              }
                              placeholder="0.00"
                              className={`${inputClass} w-32`}
                              data-testid={`invoice-line-amount-${campaign.id}`}
                            />
                          </div>
                        )}
                      </div>
                      {gate ? (
                        gate.waived ? (
                          <p className="mt-1.5 text-xs text-[#1B6B3A]" data-testid={`invoice-line-status-${campaign.id}`}>
                            Ready to invoice — AnyTribe waived the reporting requirement.
                          </p>
                        ) : gate.ready ? (
                          <p className="mt-1.5 text-xs text-[#1B6B3A]" data-testid={`invoice-line-status-${campaign.id}`}>
                            {gate.is_ugc
                              ? "Ready to invoice — UGC campaign, no posting links or insights required."
                              : "Ready to invoice."}
                          </p>
                        ) : (
                          <p className="mt-1.5 text-xs font-semibold text-[#A85A05]" data-testid={`invoice-line-status-${campaign.id}`}>
                            {gate.blockers.join(" · ")} —{" "}
                            <Link to="/creator" className="underline" data-testid={`invoice-line-fix-${campaign.id}`}>
                              complete your reporting
                            </Link>{" "}
                            to invoice this campaign.
                          </p>
                        )
                      ) : isUgc(campaign.category) ? (
                        <p className="mt-1.5 text-xs body-text" data-testid={`invoice-line-status-${campaign.id}`}>
                          UGC campaign — no posting links or insights required.
                        </p>
                      ) : outstanding > 0 ? (
                        <p className="mt-1.5 text-xs font-semibold text-[#B3261E]" data-testid={`invoice-line-status-${campaign.id}`}>
                          {outstanding} post{outstanding === 1 ? "" : "s"} still need insights —{" "}
                          <Link to="/creator/campaigns" className="underline">
                            complete them
                          </Link>
                          . You can still invoice now; AnyTribe will see the outstanding items.
                        </p>
                      ) : (
                        <p className="mt-1.5 text-xs text-[#1B6B3A]" data-testid={`invoice-line-status-${campaign.id}`}>
                          Insights up to date.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Field>
            {selectedLines.length > 0 && (
              <div className="rounded-xl border border-[#EFEAE1] bg-[#FBF9F5] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                  Invoice total
                </p>
                <p className="mt-1 text-2xl font-extrabold tabular" data-testid="invoice-total">
                  RM {totalRM.toFixed(2)}
                </p>
                <p className="mt-1 text-xs body-text">
                  Calculated from {selectedLines.length} campaign
                  {selectedLines.length === 1 ? "" : "s"}.
                </p>
              </div>
            )}
            {creatorOptions && (
              <Field label="Creator" required>
                <select
                  value={creatorId}
                  onChange={(e) => setCreatorId(e.target.value)}
                  required
                  className={inputClass}
                  data-testid="invoice-creator-select"
                >
                  <option value="">Select a creator</option>
                  {creatorOptions.map((c) => (
                    <option key={c.creator_id} value={c.creator_id}>
                      {c.creator_name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Invoice number" required>
              <input
                required
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="INV-2026-014"
                className={inputClass}
                data-testid="invoice-number-input"
              />
            </Field>
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Invoice date" required>
            <input
              type="date"
              required
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className={inputClass}
              data-testid="invoice-date-input"
            />
          </Field>
          <Field label="Due date (optional)">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={inputClass}
              data-testid="invoice-due-date-input"
            />
          </Field>
        </div>

        {isVersion && (
        <Field label="Total amount (RM)" required hint="Malaysian Ringgit only, e.g. 1250.00">
          <input
            required
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="1250.00"
            className={inputClass}
            data-testid="invoice-amount-input"
          />
        </Field>
        )}

        <Field label="Invoice document" required hint="PDF only — photos and screenshots are not accepted.">
          <Uploader key={uploaderKey} kind="invoice" files={files} onChange={setFiles} testId="invoice-uploader" />
        </Field>

        <Field label="Note (optional)">
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputClass}
            data-testid="invoice-note-input"
          />
        </Field>

        <div className="flex justify-end gap-2.5 pt-1">
          <Button variant="outline" onClick={onClose} testId="invoice-cancel-button">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="invoice-submit-button">
            {isVersion ? "Submit corrected version" : "Submit invoice"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const BILLING_FIELDS = [
  { key: "full_name", label: "Full name (as per IC)" },
  { key: "ic_number", label: "IC number" },
  { key: "address", label: "Address" },
  { key: "phone", label: "Phone number" },
  { key: "bank_name", label: "Bank name" },
  { key: "bank_account", label: "Bank account number" },
];

/** Billing details entered once, then an invoice PDF produced and submitted in one step. */
export function GenerateInvoiceModal({ open, campaigns, readiness, onClose, onSaved }) {
  const [billing, setBilling] = useState(null);
  const [lines, setLines] = useState({});
  const [number, setNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayKL());
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLines({});
    setNumber("");
    setInvoiceDate(todayKL());
    setDueDate("");
    setNote("");
    setError("");
    setBilling(null);
    api
      .get("/account/billing")
      .then((r) => setBilling(r.data))
      .catch((e) => {
        setError(apiError(e));
        setBilling({});
      });
  }, [open]);

  const selected = Object.entries(lines).map(([id, amount]) => ({
    id,
    amount,
    name: (campaigns || []).find((c) => c.id === id)?.name || "Campaign",
  }));
  const totalRM = selected.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const missing = BILLING_FIELDS.filter((f) => !String(billing?.[f.key] || "").trim());

  const saveBilling = async () => {
    const payload = {};
    BILLING_FIELDS.forEach((f) => {
      payload[f.key] = String(billing?.[f.key] || "").trim();
    });
    const { data } = await api.put("/account/billing", payload);
    setBilling(data);
    return data;
  };

  const validate = () => {
    if (missing.length > 0) {
      setError(`Add your ${missing.map((f) => f.label.toLowerCase()).join(", ")} first.`);
      return false;
    }
    if (!number.trim()) {
      setError("Enter the invoice number you want on this invoice.");
      return false;
    }
    if (selected.length === 0) {
      setError("Select at least one campaign to invoice.");
      return false;
    }
    const bad = selected.filter((l) => !(Number(l.amount) > 0));
    if (bad.length > 0) {
      setError(`Enter the amount for ${bad.map((l) => l.name).join(", ")}.`);
      return false;
    }
    return true;
  };

  const preview = async () => {
    setError("");
    if (!validate()) return;
    setBusy("preview");
    try {
      await saveBilling();
      const response = await api.post(
        "/invoices/preview",
        {
          lines: selected.map((l) => ({ campaign_id: l.id, amount: Number(l.amount) })),
          invoice_number: number.trim(),
          invoice_date: invoiceDate,
          due_date: dueDate || null,
          note: note.trim() || null,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!validate() || busy) return;
    setBusy("submit");
    try {
      await saveBilling();
      await api.post("/invoices", {
        lines: selected.map((l) => ({ campaign_id: l.id, amount: Number(l.amount) })),
        invoice_number: number.trim(),
        invoice_date: invoiceDate,
        due_date: dueDate || null,
        note: note.trim() || null,
        generate: true,
      });
      toast.success("Invoice generated and submitted");
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Generate my invoice" wide testId="generate-invoice-modal">
      {billing === null ? (
        <Loading label="Loading your billing details…" />
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="generate-invoice-form">
          <ErrorNote message={error} />
          <Note tone="neutral">
            We build the invoice for you with AnyTribe as the bill-to party and your details as the
            payee, then attach it to your submission. Your details are kept on your profile — you
            only enter them once.
          </Note>

          <div>
            <h3 className="text-sm font-extrabold">Your billing details</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {BILLING_FIELDS.map((field) => (
                <Field key={field.key} label={field.label} required>
                  <input
                    value={billing?.[field.key] || ""}
                    onChange={(e) =>
                      setBilling((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                    className={inputClass}
                    data-testid={`billing-${field.key}-input`}
                  />
                </Field>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-extrabold">Campaigns on this invoice</h3>
            <div className="mt-3 space-y-2" data-testid="generate-campaign-lines">
              {(campaigns || []).map((campaign) => {
                const gate = (readiness || []).find((r) => r.campaign_id === campaign.id);
                const blocked = Boolean(gate && !gate.ready);
                const picked = lines[campaign.id] !== undefined;
                return (
                  <div
                    key={campaign.id}
                    className={`rounded-xl border p-3 transition-colors ${
                      blocked
                        ? "border-[#EFEAE1] bg-[#FBFAF7] opacity-80"
                        : picked
                          ? "border-[#F2811D] bg-[#FFF8F1]"
                          : "border-[#EFEAE1]"
                    }`}
                    data-testid={`generate-line-${campaign.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <label className="flex items-center gap-2 text-sm font-semibold">
                        <input
                          type="checkbox"
                          checked={picked}
                          disabled={blocked}
                          onChange={(e) =>
                            setLines((prev) => {
                              const next = { ...prev };
                              if (e.target.checked) next[campaign.id] = "";
                              else delete next[campaign.id];
                              return next;
                            })
                          }
                          data-testid={`generate-line-check-${campaign.id}`}
                        />
                        {campaign.name}
                      </label>
                      {picked && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold body-text">RM</span>
                          <input
                            inputMode="decimal"
                            value={lines[campaign.id]}
                            onChange={(e) =>
                              setLines((prev) => ({ ...prev, [campaign.id]: e.target.value }))
                            }
                            placeholder="0.00"
                            className={`${inputClass} w-32`}
                            data-testid={`generate-line-amount-${campaign.id}`}
                          />
                        </div>
                      )}
                    </div>
                    {blocked && (
                      <p
                        className="mt-1.5 text-xs font-semibold text-[#A85A05]"
                        data-testid={`generate-line-status-${campaign.id}`}
                      >
                        {gate.blockers.join(" · ")} —{" "}
                        <Link to="/creator" className="underline">
                          complete your reporting
                        </Link>{" "}
                        to invoice this campaign.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div className="mt-3 rounded-xl border border-[#EFEAE1] bg-[#FBF9F5] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                  Invoice total
                </p>
                <p className="mt-1 text-2xl font-extrabold tabular" data-testid="generate-invoice-total">
                  RM {totalRM.toFixed(2)}
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Invoice number" required>
              <input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="INV-2026-014"
                className={inputClass}
                data-testid="generate-invoice-number-input"
              />
            </Field>
            <Field label="Invoice date" required>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className={inputClass}
                data-testid="generate-invoice-date-input"
              />
            </Field>
            <Field label="Due date (optional)">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={inputClass}
                data-testid="generate-invoice-due-input"
              />
            </Field>
          </div>

          <Field label="Note (optional)">
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputClass}
              data-testid="generate-invoice-note-input"
            />
          </Field>

          <div className="flex flex-wrap justify-end gap-2.5">
            <Button variant="outline" onClick={onClose} testId="generate-invoice-cancel">
              Cancel
            </Button>
            <Button
              variant="outline"
              busy={busy === "preview"}
              onClick={preview}
              testId="generate-invoice-preview"
            >
              <Download className="h-4 w-4" />
              Preview the invoice
            </Button>
            <Button type="submit" busy={busy === "submit"} testId="generate-invoice-submit">
              Generate and submit
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
